import type { User, Proposal, Client, Service, Plan, Brand, GalleryItem, FAQ, Notification } from '@omnes/shared/schemas';

const MOCK_ENABLED = import.meta.env.VITE_MOCK_ENABLED === 'true';

const mockUser: User = {
  id: 1, name: 'Administrador Omnes', email: 'admin@omnesholding.cl',
  role: 'admin', brand_id: 1, avatar_url: null, phone: null,
  is_active: true, last_login_at: new Date().toISOString(),
  created_at: '2024-01-01T00:00:00Z', updated_at: new Date().toISOString(),
};

const mockClient: Client = {
  id: 1, brand_id: 1, name: 'Ministerio Generación', ministry: 'Ministerio de Comunicación',
  email: 'contacto@ministeriogeneracion.cl', phone: '+56912345678', city: 'Santiago',
  address: 'Av. Libertador 1234', status: 'client', assigned_user_id: 1,
  notes: 'Ministerio con presencia digital activa', tags: ['ministerio', 'premium'],
  created_at: '2024-01-15T00:00:00Z', updated_at: new Date().toISOString(),
};

const mockProposal: Proposal = {
  id: 1, brand_id: 1, client_id: 1, slug: 'ministerio-generacion',
  title: 'Propuesta Comercial - Radio Generación Fe',
  status: 'sent',
  cover_data: {
    logo_url: '',
    client_name: 'Ministerio Generación',
    welcome_message: 'Produce tu propio programa profesional en línea',
    tagline: 'Producción de Programas Online en Vivo',
  },
  cover_letter: `Estimado equipo del Ministerio Generación,

Nos complace presentar esta propuesta comercial diseñada especialmente para ustedes.

Radio Generación Fe es una plataforma de comunicación que permite crear programas online profesionales combinando radio, tecnología y producción digital.

No vendemos solamente una señal radial. Entregamos una solución completa de producción, transmisión y soporte técnico para que ustedes puedan tener su propio programa profesional, sin preocuparse de la parte técnica.

Nosotros nos encargamos de la configuración, operación, producción y soporte. Ustedes solo necesitan enfocarse en su mensaje.`,
  history_data: {
    mission: 'Ayudar a organizaciones y comunicadores a transmitir su mensaje mediante herramientas profesionales de comunicación digital.',
    vision: 'Ser la plataforma líder en producción de programas online para organizaciones en Latinoamérica.',
    values: ['Fe', 'Excelencia', 'Innovación', 'Servicio', 'Comunidad'],
    story: 'Radio Generación Fe nació con la visión de democratizar el acceso a la comunicación profesional. Creemos que toda organización merece tener presencia en medios sin necesidad de infraestructura costosa.',
  },
  who_we_are: 'Somos una plataforma de comunicación que permite crear programas online profesionales combinando radio, tecnología y producción digital. El cliente no necesita preocuparse de la parte técnica. Nosotros nos encargamos de la configuración, operación, producción y soporte.',
  services_data: [
    {
      name: 'Plan Audio',
      description: 'Programa Online En Vivo. Programa profesional en formato radial para comenzar presencia en medios.',
      icon: 'radio',
      features: [
        'Programa en vivo',
        'Conductor desde cualquier ubicación',
        'Configuración técnica remota',
        'Operación de audio',
        'Control de niveles',
        'Musicalización de apoyo',
        'Salida por Radio Generación Fe',
        'Soporte técnico durante la transmisión',
      ],
    },
    {
      name: 'Plan Audio + Video',
      description: 'Programa Online Multimedia. Convierte un programa radial en una experiencia audiovisual completa.',
      icon: 'video',
      features: [
        'Todo el Plan Audio',
        'Cámaras Full HD',
        'Operación de cámaras',
        'Control de video',
        'Escenas profesionales',
        'Logos y gráficas',
        'Transmisión redes sociales',
        'Facebook Live',
        'YouTube Live',
        'Grabación audiovisual',
      ],
    },
    {
      name: 'Plan Premium',
      description: 'Programa Digital Profesional. La solución completa para organizaciones que quieren construir una presencia digital.',
      icon: 'star',
      features: [
        'Todo el Plan Audio + Video',
        'Diseño visual personalizado',
        'Intro del programa',
        'Outro',
        'Cortinas musicales',
        'Pantallas gráficas',
        'Animaciones',
        'Edición del contenido',
        'Miniaturas YouTube',
        'Clips para redes sociales',
      ],
    },
  ],
  plans_data: [
    {
      name: 'Plan Mensual',
      description: 'Sin compromiso de permanencia. Flexibilidad mes a mes.',
      price: 49990,
      interval: 'monthly',
      features: ['Producción técnica', 'Configuración remota', 'Control de audio', 'Soporte técnico'],
      benefits: ['Sin compromiso a largo plazo', 'Cancelación flexible'],
      bonuses: [],
      is_popular: false,
    },
    {
      name: 'Plan Trimestral',
      description: 'Mes adicional sin costo al completar el período contratado y mantener continuidad.',
      price: 49990,
      interval: 'quarterly',
      features: ['Producción técnica', 'Configuración remota', 'Control de audio', 'Control de video', 'Soporte prioritario'],
      benefits: ['1 mes adicional sin costo', 'Al mantener continuidad'],
      bonuses: ['Mes gratis por continuidad'],
      is_popular: true,
    },
    {
      name: 'Plan Semestral',
      description: 'Beneficios especiales por permanencia y mayor ahorro.',
      price: 49990,
      interval: 'semiannual',
      features: ['Producción técnica', 'Configuración remota', 'Control de audio', 'Control de video', 'Soporte prioritario', 'Edición de contenido'],
      benefits: ['Beneficios especiales por permanencia', 'Ahorro significativo'],
      bonuses: ['Beneficios especiales por permanencia'],
      is_popular: false,
    },
    {
      name: 'Plan Anual',
      description: 'Mayor beneficio comercial y prioridad de soporte. La inversión más inteligente.',
      price: 49990,
      interval: 'annual',
      features: ['Producción técnica completa', 'Configuración remota', 'Control de audio y video', 'Edición de contenido', 'Soporte prioritario 24/7', 'Diseño visual'],
      benefits: ['Mayor beneficio comercial', 'Prioridad de soporte', 'Inversión más rentable'],
      bonuses: ['Prioridad de soporte', 'Mayor beneficio comercial'],
      is_popular: false,
    },
  ],
  benefits_data: [
    { title: 'No necesitan estudio propio', description: 'El conductor realiza su programa desde cualquier ubicación: hogar, oficina, iglesia o cualquier lugar.', icon: 'home', date: '' },
    { title: 'No necesitan operadores técnicos', description: 'Radio Generación Fe se encarga de toda la operación técnica durante la transmisión.', icon: 'settings', date: '' },
    { title: 'Transmiten desde cualquier lugar', description: 'Configuración remota completa. Solo necesita conexión a internet.', icon: 'globe', date: '' },
    { title: 'Producción profesional', description: 'Calidad broadcast con control de audio, video, escenas y gráficas.', icon: 'mic', date: '' },
    { title: 'Mayor alcance', description: 'Presencia simultánea en Radio Generación Fe, Facebook, YouTube y plataformas digitales.', icon: 'trending-up', date: '' },
    { title: 'Contenido reutilizable', description: 'Cada programa puede ser editado, recortado y distribuido en múltiples plataformas.', icon: 'repeat', date: '' },
  ],
  additional_services_data: [
    { name: 'Edición del programa', description: 'Edición profesional del contenido grabado para optimizar la calidad del programa final.', icon: 'edit', price: 15000 },
    { name: 'Reel para redes', description: 'Creación de reels optimizados para Instagram, TikTok y otras plataformas de video corto.', icon: 'film', price: 10000 },
    { name: 'Short YouTube', description: 'Videos cortos optimizados para YouTube Shorts con edición y gráficas profesionales.', icon: 'youtube', price: 10000 },
    { name: 'Diseño miniatura', description: 'Diseño de miniaturas profesionales para YouTube y plataformas de podcast.', icon: 'image', price: 8000 },
    { name: 'Spot radial', description: 'Producción de spots publicitarios para difusión en Radio Generación Fe.', icon: 'radio', price: 15000 },
    { name: 'Página web', description: 'Sitio web profesional para el ministerio u organización con diseño personalizado.', icon: 'globe', price: 250000 },
  ],
  faq_data: [
    { question: '¿Necesito un estudio propio?', answer: 'No. El conductor puede realizar su programa desde cualquier ubicación con conexión a internet: hogar, oficina, iglesia o cualquier otro lugar.' },
    { question: '¿Qué equipo necesito?', answer: 'Solo necesita una computadora, micrófono y conexión a internet estable. Nosotros nos encargamos del resto.' },
    { question: '¿Puedo transmitir en múltiples plataformas?', answer: 'Sí. Dependiendo del plan contratado, su programa puede emitirse simultáneamente en Radio Generación Fe, Facebook, YouTube y otras plataformas digitales.' },
    { question: '¿Qué incluye la producción técnica?', answer: 'La producción incluye configuración remota, control de audio, control de video, manejo de escenas, logos, gráficas y soporte técnico durante toda la transmisión.' },
    { question: '¿Puedo gestionar mis propios auspicios?', answer: 'Sí. El conductor del programa puede gestionar sus propios auspiciadores. Radio Generación Fe cobra únicamente el servicio contratado.' },
    { question: '¿Cuánto tarda en estar activo el servicio?', answer: 'El servicio se activa en un plazo de 48 a 72 horas después de la firma del acuerdo y agendamiento del horario.' },
  ],
  gallery_data: [
    { type: 'video', url: 'https://example.com/demo-rfg.mp4', title: 'Demo Radio Generación Fe', description: 'Conoce cómo funciona nuestra plataforma de producción' },
    { type: 'image', url: 'https://example.com/estudio.jpg', title: 'Nuestro Estudio', description: 'Estudio de control de producción profesional' },
    { type: 'testimonial', url: '', title: 'Testimonio Iglesia', description: '"Radio Transformó nuestra comunicación" - Pastor Roberto Martínez' },
  ],
  simulator_config: { default_sponsors: 5, default_sponsor_value: 150000, default_monthly_cost: 49990, currency: 'CLP' },
  signature_data: null, settings: null, sent_at: '2024-03-01T00:00:00Z', viewed_at: '2024-03-02T00:00:00Z',
  accepted_at: null, rejected_at: null, view_count: 12, total_read_time: 840,
  last_viewed_at: '2024-03-05T00:00:00Z', created_by: 1, created_at: '2024-02-28T00:00:00Z', updated_at: '2024-03-05T00:00:00Z', client: mockClient,
};

function delay(ms = 200): Promise<void> { return new Promise(r => setTimeout(r, ms)); }

class MockApiClient {
  async get<T>(endpoint: string): Promise<T> {
    await delay();
    if (endpoint === '/auth/me') return { ...mockUser } as T;
    if (endpoint === '/brands') return [{ id: 1, name: 'Omnes Holding SpA', commercial_name: 'Radio Generación Fe', logo_url: null, primary_color: '#ed8914', secondary_color: '#0f172a', domain: null, is_active: true, settings: null, created_at: '2024-01-01', updated_at: '2024-01-01' }] as T;
    if (endpoint.startsWith('/proposals/slug/')) return { ...mockProposal } as T;
    if (endpoint.startsWith('/proposals/stats/')) return { total: 5, sent: 2, viewed: 1, accepted: 1, rejected: 0, draft: 1, conversion_rate: '20.0', avg_read_time_seconds: 840 } as T;
    if (endpoint === '/proposals') return { data: [{ ...mockProposal }], meta: { page: 1, limit: 20, total: 1, totalPages: 1 } } as T;
    if (endpoint.startsWith('/proposals/')) return { ...mockProposal } as T;
    if (endpoint === '/clients') return { data: [{ ...mockClient }], meta: { page: 1, limit: 20, total: 1, totalPages: 1 } } as T;
    if (endpoint.startsWith('/clients/')) return { ...mockClient } as T;
    if (endpoint === '/services') return [mockProposal.services_data![0], mockProposal.services_data![1], mockProposal.services_data![2]] as any as T;
    if (endpoint === '/plans') return mockProposal.plans_data!.map((p, i) => ({ ...p, id: i + 1, brand_id: 1, period: p.interval, price: p.price, features: p.features, benefits: p.benefits, bonuses: p.bonuses, is_popular: p.is_popular, is_active: true, sort_order: i, created_at: '2024-01-01', updated_at: '2024-01-01', description: p.description })) as any as T;
    if (endpoint === '/benefits') return mockProposal.benefits_data!.map((b, i) => ({ ...b, id: i + 1, brand_id: 1, sort_order: i, is_active: true, created_at: '2024-01-01', updated_at: '2024-01-01', date: b.date })) as any as T;
    if (endpoint === '/faqs') return mockProposal.faq_data!.map((f, i) => ({ ...f, id: i + 1, brand_id: 1, category: 'general', sort_order: i, is_active: true, created_at: '2024-01-01', updated_at: '2024-01-01' })) as any as T;
    if (endpoint === '/gallery') return [] as T;
    if (endpoint === '/notifications') return { data: [{ id: 1, user_id: 1, type: 'proposal_opened' as const, title: 'Propuesta abierta', message: 'Ministerio Generación abrió la propuesta', data: null, is_read: false, read_at: null, created_at: new Date().toISOString() }], unreadCount: 1 } as T;
    if (endpoint === '/users') return [{ ...mockUser }] as T;
    return null as T;
  }
  async post<T>(endpoint: string, _data?: unknown): Promise<T> {
    await delay();
    if (endpoint === '/auth/login') return { user: { ...mockUser }, token: 'mock-' + Date.now() } as T;
    if (endpoint === '/auth/logout') return { message: 'ok' } as T;
    if (endpoint.includes('/sign')) return { message: 'Propuesta firmada' } as T;
    return { success: true } as T;
  }
  async put<T>(_e: string, d?: unknown): Promise<T> { await delay(); return d as T; }
  async patch<T>(_e: string, _d?: unknown): Promise<T> { await delay(); return { success: true } as T; }
  async delete<T>(_e: string): Promise<T> { await delay(); return { success: true } as T; }
}

const mock = new MockApiClient();

export const apiClient = {
  baseUrl: '/api',
  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (MOCK_ENABLED) {
      const m = (options.method || 'GET').toUpperCase();
      if (m === 'GET') return mock.get<T>(endpoint);
      if (m === 'POST') return mock.post<T>(endpoint, options.body ? JSON.parse(options.body as string) : undefined);
      if (m === 'PUT') return mock.put<T>(endpoint, options.body ? JSON.parse(options.body as string) : undefined);
      if (m === 'PATCH') return mock.patch<T>(endpoint, options.body ? JSON.parse(options.body as string) : undefined);
      if (m === 'DELETE') return mock.delete<T>(endpoint);
    }
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(options.headers as Record<string, string>) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${this.baseUrl}${endpoint}`, { ...options, headers });
    if (!response.ok) { const e = await response.json().catch(() => ({ message: 'Error' })); throw new Error(e.error?.message || e.message || `HTTP ${response.status}`); }
    if (response.status === 204) return undefined as T;
    const json = await response.json();
    if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
      return json.data as T;
    }
    return json as T;
  },
  get<T>(e: string) { return this.request<T>(e, { method: 'GET' }); },
  post<T>(e: string, d: unknown) { return this.request<T>(e, { method: 'POST', body: JSON.stringify(d) }); },
  put<T>(e: string, d: unknown) { return this.request<T>(e, { method: 'PUT', body: JSON.stringify(d) }); },
  patch<T>(e: string, d: unknown) { return this.request<T>(e, { method: 'PATCH', body: JSON.stringify(d) }); },
  delete<T>(e: string) { return this.request<T>(e, { method: 'DELETE' }); },
};

export const authApi = {
  async login(email: string, password: string) { return apiClient.post<{ user: User; token: string }>('/auth/login', { email, password }); },
  async register(d: { name: string; email: string; password: string; role?: string }) { return apiClient.post<{ user: User; token: string }>('/auth/register', d); },
  async getCurrentUser() { return apiClient.get<User>('/auth/me'); },
  async logout() { return apiClient.post('/auth/logout', {}); },
};

export const proposalsApi = {
  list(p?: { page?: number; limit?: number; status?: string; clientId?: string }) {
    const q = new URLSearchParams(); if (p?.page) q.set('page', String(p.page)); if (p?.status) q.set('status', p.status);
    return apiClient.get<{ data: Proposal[]; meta: any }>(`/proposals?${q}`);
  },
  get(id: string) { return apiClient.get<Proposal>(`/proposals/${id}`); },
  getBySlug(s: string) { return apiClient.get<Proposal>(`/proposals/slug/${s}`); },
  create(d: any) { return apiClient.post<Proposal>('/proposals', d); },
  update(id: string, d: any) { return apiClient.put<Proposal>(`/proposals/${id}`, d); },
  delete(id: string) { return apiClient.delete(`/proposals/${id}`); },
  duplicate(id: string) { return apiClient.post<Proposal>(`/proposals/${id}/duplicate`, {}); },
  trackReadTime(id: string, s: number) { return apiClient.post(`/proposals/${id}/track-read-time`, { seconds: s }); },
  sign(id: string, d: any) { return apiClient.post(`/proposals/${id}/sign`, d); },
  getStats() { return apiClient.get<any>('/proposals/stats/overview'); },
};

export const clientsApi = {
  list(p?: { page?: number; limit?: number; status?: string; search?: string }) {
    const q = new URLSearchParams(); if (p?.page) q.set('page', String(p.page)); if (p?.status) q.set('status', p.status);
    return apiClient.get<{ data: Client[]; meta: any }>(`/clients?${q}`);
  },
  get(id: string) { return apiClient.get<Client>(`/clients/${id}`); },
  create(d: any) { return apiClient.post<Client>('/clients', d); },
  update(id: string, d: any) { return apiClient.put<Client>(`/clients/${id}`, d); },
  delete(id: string) { return apiClient.delete(`/clients/${id}`); },
};

export const servicesApi = {
  list() { return apiClient.get<Service[]>('/services'); },
  create(d: any) { return apiClient.post<Service>('/services', d); },
  update(id: string, d: any) { return apiClient.put<Service>(`/services/${id}`, d); },
  delete(id: string) { return apiClient.delete(`/services/${id}`); },
};

export const plansApi = {
  list() { return apiClient.get<Plan[]>('/plans'); },
  create(d: any) { return apiClient.post<Plan>('/plans', d); },
  update(id: string, d: any) { return apiClient.put<Plan>(`/plans/${id}`, d); },
  delete(id: string) { return apiClient.delete(`/plans/${id}`); },
};

export const brandsApi = {
  list() { return apiClient.get<Brand[]>('/brands'); },
  create(d: any) { return apiClient.post<Brand>('/brands', d); },
  update(id: string, d: any) { return apiClient.put<Brand>(`/brands/${id}`, d); },
};

export const galleryApi = {
  list() { return apiClient.get<GalleryItem[]>('/gallery'); },
  create(d: any) { return apiClient.post<GalleryItem>('/gallery', d); },
  update(id: string, d: any) { return apiClient.put<GalleryItem>(`/gallery/${id}`, d); },
  delete(id: string) { return apiClient.delete(`/gallery/${id}`); },
};

export const faqApi = {
  list() { return apiClient.get<FAQ[]>('/faqs'); },
  create(d: any) { return apiClient.post<FAQ>('/faqs', d); },
  update(id: string, d: any) { return apiClient.put<FAQ>(`/faqs/${id}`, d); },
  delete(id: string) { return apiClient.delete(`/faqs/${id}`); },
};

export const notificationsApi = {
  list(p?: { page?: number; limit?: number; unreadOnly?: boolean }) {
    const q = new URLSearchParams(); if (p?.unreadOnly) q.set('unreadOnly', 'true');
    return apiClient.get<{ data: Notification[]; unreadCount: number }>(`/notifications?${q}`);
  },
  markAsRead(id: string) { return apiClient.patch(`/notifications/${id}/read`, {}); },
  markAllAsRead() { return apiClient.patch('/notifications/read-all', {}); },
};

export const usersApi = {
  list() { return apiClient.get<User[]>('/users'); },
  create(d: any) { return apiClient.post<User>('/users', d); },
  update(id: string, d: any) { return apiClient.put<User>(`/users/${id}`, d); },
  delete(id: string) { return apiClient.delete(`/users/${id}`); },
};